// field path can be string or array

import {
  FooterData,
  HeaderData,
  PlatformCosmicModule,
  SeoHeaderData,
} from '@finn/ui-cosmic';

import { Modal } from './wizard';

export type Primitive = string | number | boolean;

export type Slot = {
  start: string;
  end: string;
  value: string;
  formatted: string;
  formatted_time: string;
  formatted_date: string;
};

export type Address = {
  first_name?: string;
  last_name?: string;
  house_number: string;
  street: string;
  city: string;
  zip: string;
  address_extra?: string;
  phone_number?: string;
};

export type Availability = {
  date: string;
  max_deliveries: number;
  available_slots: Slot[];
};

export type HandoverData = {
  is_swap: boolean;
  // the last recent choice customers made about what date is preferred
  preferred_delivery_date: string;
  // if a delivery is scheduled, this is its date
  // if the handover happened already, this is the date it happened at
  planned_delivery_date: string | null;
  address: Address;
  fee: number;
  slot: Slot;
};

export type HandoverMetadata = {
  address_changeable: boolean;
  is_swap_available: boolean;
  availabilities: Availability[];
  change_fee: number;
  availability_confidence?: {
    deviation_in_weeks: number;
  };
};

export type PreReturnAppraisal = {
  subscription_id: string;
  appointment?: {
    date: string;
    provider: string;
  };
  options?: {
    earliest: string;
    latest: string;
  };
};

export enum ReturnType {
  DELIVERY = 'SELF_RETURN',
  PICKUP = 'PICKUP',
}

export enum ReturnStatus {
  REQUEST_RECEIVED = 'request_received',
  IN_PREPARATION = 'in_preparation',
  CONFIRMED = 'confirmed',
}

export type CompoundLocation = {
  id: string;
  type: string;
  address_complete: string;
  name_short: string;
  address_name: string;
  address_street: string;
  address_city: string;
  address_zip_code: string;
  address_country: string;
  address_house_number: string;
  opening_hour: string;
  closing_hour: string;
  contact_email: string;
  contact_phone_number: string;
  is_return_available: boolean;

  // new names
  name: string;
  short: string;
  street: string;
  house_number: string;
  zip: string;
  city: string;
};

export type ReturnData = {
  is_swap: boolean;
  preferred_delivery_date: string | null;
  planned_delivery_date: string | null;
  type: ReturnType | null;
  address: Address;
  fee: number;
  slot: Slot;
};

export type ReturnMetadata = {
  availabilities: Availability[];
  availability_type: 'PICKUP' | 'SELF_RETURN';
  return_fee: number;
  compound_locations: CompoundLocation[];
};

// array used for fallbacks, for example ['{deal.handover_street}', '{deal.street}']
export type FieldPath = string | string[];

export type ValueType =
  | 'days'
  | 'date'
  | 'currency'
  | 'distance'
  | 'message'
  | 'date_month_year'
  | 'map'
  | 'text';

export type MetadataContent = {
  seo_header?: SeoHeaderData;
  page_title?: string;
  smart_banners?: {
    deepLink?: string;
    withAuth?: boolean;
    autohide?: boolean;
  };
  header?: {
    metadata: HeaderData;
  };
  modules?: PlatformCosmicModule[];
  modals?: Modal[];
  steps?: PlatformCosmicModule[];
  global_modules_config?: {
    gap_between_modules?: number;
    use_shared_report_damage?: {
      finn_users: boolean;
      enabled: boolean;
    };
    use_shared_return?: {
      finn_users: boolean;
      enabled: boolean;
      max_subscriptions: number;
    };
  };
  footer?: {
    metadata: FooterData;
  };
};

export type StaticSelfServiceObject = {
  id: string;
  slug: string;
  type: string;
  locale: string;
  metadata: MetadataContent;
};
